import { useState, useCallback, useContext } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { ruutsApi } from '../services/ruutsApi'
import AppContext from '../context/appContext'
import { queryClient } from '../queryClient'
import { useProcessLoadingWithConfirmation } from '../utils/Loading/useProcessLoadingWithConfirmation'
import { sortHubUsersCurrentFirst } from '../utils/sortHubUsersCurrentFirst'
import { hubUsersQueryOptions } from './data/useHubUsers'
import { useAddMonitoringEvent, useCompareGpsData } from './data/useMonitoringEvents'

export const useEventCreation = onSuccess => {
  const { currentFarm } = useContext(AppContext)
  const { user } = useAuth0()
  const { processLoadingWithConfirmation } = useProcessLoadingWithConfirmation()
  const compareGps = useCompareGpsData()
  const addEvent = useAddMonitoringEvent(currentFarm?.id)

  const [loading, setLoading] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  const [eventData, setEventData] = useState({})
  const [eventStartDate, setEventStartDate] = useState('')
  const [eventEndDate, setEventEndDate] = useState('')
  const [file, setFile] = useState(null)
  const [hasGpxFile, setHasGpxFile] = useState(true)

  const [monitoringSitesWithTrackPoints, setMonitoringSitesWithTrackPoints] = useState([])
  const [waypoints, setWaypoints] = useState([])

  const [hubUsers, setHubUsers] = useState([])
  const [assignedTo, setAssignedTo] = useState([])

  const resetForm = () => {
    setFile(null)
    setLoading(false)
    setUploadError(null)
    setEventData({})
    setEventStartDate('')
    setEventEndDate('')
    setAssignedTo([hubUsers[0]?.email].filter(Boolean))
    setHasGpxFile(true)
    setMonitoringSitesWithTrackPoints([])
    setWaypoints([])
  }

  // Imperative rather than useHubUsers: the dialog fetches on open (a transition handler),
  // not on render.
  const fetchHubUsers = useCallback(async () => {
    // ensureQueryData has no `enabled`, so the hubId gate useHubUsers gets for free is explicit
    // here — otherwise the request goes out filtering on the literal string "undefined".
    // Clearing rather than returning bare: a farm with no hub must not keep offering the
    // previous hub's assignees.
    if (!currentFarm?.hubId) {
      setHubUsers([])
      setAssignedTo([])
      return
    }

    let users
    try {
      users = await queryClient.ensureQueryData(hubUsersQueryOptions(currentFarm.hubId))
    } catch {
      // The global query error bridge already reported it. Swallowed rather than rethrown
      // because this runs as the dialog's MUI onEnter, where a rejection goes unhandled.
      return
    }

    const sortedUsers = sortHubUsersCurrentFirst(users, user?.email)
    setHubUsers(sortedUsers)
    setAssignedTo([sortedUsers[0]?.email].filter(Boolean))
  }, [currentFarm?.hubId, user?.email])

  const getMonitoringSites = useCallback(async () => {
    try {
      if (!currentFarm?.id) return []

      const sites = await ruutsApi.monitoringSites.getMonitoringOnly({ farmId: currentFarm.id })
      return sites
    } catch (error) {
      setUploadError(error)
      return []
    }
  }, [currentFarm?.id])

  const validateGpxFile = async () => {
    try {
      if (!file) return false

      setLoading(true)
      const { monitoringSitesWithResults, waypoints: waypointsResult } =
        await compareGps.mutateAsync({
          farm: currentFarm,
          file,
          eventStartDate,
          eventEndDate,
        })

      setMonitoringSitesWithTrackPoints(monitoringSitesWithResults)
      setWaypoints(waypointsResult)
      setUploadError(null)
      return true
    } catch (error) {
      setUploadError(error)
      return false
    } finally {
      setLoading(false)
      setEventData({})
    }
  }

  const createEvent = async () => {
    setLoading(true)
    // No catch: processLoading reports failures through the loading modal and never rethrows.
    try {
      await processLoadingWithConfirmation({
        confirmationTitle: 'Crear evento',
        confirmationMessage:
          'Se creará el evento y se confirmarán los sitios con las ubicaciones proporcionadas. ¿Desea continuar?',
        loadingMessage: 'Creando evento...',
        errorMessage: 'Error al crear el evento.',
        doAction: async ({ dryRun }) => {
          if (dryRun) return

          await addEvent.mutateAsync({
            farmId: currentFarm.id,
            eventData,
            eventStartDate,
            eventEndDate,
            isBackdatedEvent: true,
            gpsData: file,
            siteConfirmationRequired: true,
          })

          resetForm()
          if (onSuccess) onSuccess()
        },
      })
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    uploadError,
    eventData,
    eventStartDate,
    eventEndDate,
    file,
    hasGpxFile,
    monitoringSitesWithTrackPoints,
    waypoints,
    hubUsers,
    assignedTo,
    currentFarm,

    setEventData,
    setEventStartDate,
    setEventEndDate,
    setFile,
    setAssignedTo,
    setHasGpxFile,
    setUploadError,
    setMonitoringSitesWithTrackPoints,
    setWaypoints,
    setLoading,

    fetchHubUsers,
    getMonitoringSites,
    validateGpxFile,
    createEvent,
    resetForm,
  }
}
